import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  cn,
  Icon,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@nexus/ui";
import {
  eventToDomainTopic,
  isKnownEventName,
  KNOWN_EVENT_NAMES,
  TRIGGERABLE_EVENTS,
  TriggerableEventImpacts,
  type DomainTopic,
  type EventName,
  type TriggerableEvent,
} from "@nexus/contracts";
import { config } from "@/config";
import { lastEventPayloadQueryOptions } from "@/lib/queries";
import {
  blankPayloadShape,
  checkCustomPayload,
  checkPayload,
  formatPayloadText,
  formatSamplePayload,
  groupEventsByTopic,
  hasPayloadFields,
  type PayloadIssue,
} from "../lib/event-publish";

interface EventPublishSheetProps {
  isOpen: boolean;
  isPublishing: boolean;
  publishError: string | null;
  onOpenChange: (open: boolean) => void;
  onPublish: (input: { eventName: string; payload: Record<string, unknown> }) => void;
  /** Pre-selects an event on open — e.g. the Scheduled Jobs tab's "run now", which already knows which event a job publishes. Falls back to the first triggerable event. */
  initialEventName?: string;
  /** Seeds the editor with a real payload instead of the blank sample — the republish path carries the failed event's own payload over. Ignored unless `initialEventName` matches a triggerable event. */
  initialPayload?: Record<string, unknown>;
}

const PRODUCTION_ENVIRONMENT = "production";
const PAYLOAD_ROWS = 6;

type PublishMode = "template" | "custom";

/** Sentinel for the topic filter's "show every topic" option. */
const ALL_TOPICS = "all";

const CUSTOM_PAYLOAD_STATUS_ID = "event-publish-custom-payload-status";

/**
 * Fires a real event on the bus the app itself publishes to, so the only
 * safeguard that means anything is the catalog: an operator picks a name from
 * `KNOWN_EVENT_NAMES` and cannot invent one. Template mode leads with what
 * each event *does* rather than what it is called, because the name alone does
 * not tell you that one of them re-syncs every tenant from BigQuery. Scratch
 * mode trades that guidance for reach — any known event, payload sent as-is.
 */
export function EventPublishSheet({
  isOpen,
  isPublishing,
  publishError,
  onOpenChange,
  onPublish,
  initialEventName,
  initialPayload,
}: EventPublishSheetProps) {
  const { t } = useTranslation();

  const [mode, setMode] = useState<PublishMode>("template");
  const [eventName, setEventName] = useState(TRIGGERABLE_EVENTS[0]?.eventName ?? "");
  const [payloadText, setPayloadText] = useState("");
  // The sample opens with its fields blank, which the schema rightly rejects —
  // but colouring that red before anyone has typed is nagging, so the same
  // message starts as a hint and only turns into an error once it is earned.
  const [isPayloadEdited, setIsPayloadEdited] = useState(false);
  const [customEventName, setCustomEventName] = useState<EventName | null>(null);
  const [customPayloadText, setCustomPayloadText] = useState("");
  const [topicFilter, setTopicFilter] = useState<DomainTopic | typeof ALL_TOPICS>(ALL_TOPICS);

  const selected = useMemo(
    () => TRIGGERABLE_EVENTS.find((entry) => entry.eventName === eventName),
    [eventName],
  );

  // No schema exists for a scratch-mode event, so the last one published is
  // the only evidence of which fields its consumer reads.
  const lastPayloadQuery = useQuery(lastEventPayloadQueryOptions(customEventName ?? ""));
  const lastShapeText = useMemo(() => {
    const payload = lastPayloadQuery.data;
    if (!payload || Object.keys(payload).length === 0) return null;
    return JSON.stringify(blankPayloadShape(payload), null, 2);
  }, [lastPayloadQuery.data]);

  const topicGroups = useMemo(() => groupEventsByTopic(KNOWN_EVENT_NAMES), []);
  const visibleTopicGroups = useMemo(
    () =>
      topicFilter === ALL_TOPICS
        ? topicGroups
        : topicGroups.filter((group) => group.topic === topicFilter),
    [topicGroups, topicFilter],
  );

  useEffect(() => {
    if (!isOpen) return;
    const preselected = TRIGGERABLE_EVENTS.find((entry) => entry.eventName === initialEventName);
    const first = preselected ?? TRIGGERABLE_EVENTS[0];
    const carried = initialPayload ? JSON.stringify(initialPayload, null, 2) : "";
    // Most event types are not on the triggerable allowlist, so republishing
    // one means opening scratch mode on its name — the template list would
    // silently drop the event the operator asked for.
    const carriedCustom =
      !preselected && !!initialEventName && isKnownEventName(initialEventName)
        ? initialEventName
        : null;

    setMode(carriedCustom ? "custom" : "template");
    setEventName(first?.eventName ?? "");
    setPayloadText(preselected && carried ? carried : first ? formatSamplePayload(first) : "");
    // A payload carried over from a real event is input rather than a blank
    // template, so a schema complaint about it is earned from the first render.
    setIsPayloadEdited(!!preselected && carried !== "");
    setCustomEventName(carriedCustom);
    setCustomPayloadText(carriedCustom ? carried : "");
    setTopicFilter(carriedCustom ? eventToDomainTopic(carriedCustom) : ALL_TOPICS);
  }, [isOpen, initialEventName, initialPayload]);

  function pickTopic(value: DomainTopic | typeof ALL_TOPICS) {
    setTopicFilter(value);
    // Leaving the chosen event visible while the list no longer offers it
    // would let an operator publish something the filter says is out of scope.
    if (value !== ALL_TOPICS && customEventName && eventToDomainTopic(customEventName) !== value) {
      setCustomEventName(null);
    }
  }

  function pick(entry: TriggerableEvent) {
    setEventName(entry.eventName);
    setPayloadText(formatSamplePayload(entry));
    setIsPayloadEdited(false);
  }

  const check = selected ? checkPayload(selected, payloadText) : null;
  const customCheck = checkCustomPayload(customPayloadText);
  const customFieldCount = customCheck.ok ? Object.keys(customCheck.value).length : 0;
  const formattedCustomPayload = formatPayloadText(customPayloadText);
  const canPublish =
    !isPublishing &&
    (mode === "template"
      ? !!selected && check?.ok === true
      : !!customEventName && customCheck.ok);
  const isProduction = config.environment === PRODUCTION_ENVIRONMENT;

  function publish() {
    if (mode === "template") {
      if (!selected || check?.ok !== true) return;
      onPublish({ eventName: selected.eventName, payload: check.value });
      return;
    }
    if (!customEventName || !customCheck.ok) return;
    onPublish({ eventName: customEventName, payload: customCheck.value });
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t("admin.events.publish.title", "Publish event")}</SheetTitle>
          <SheetDescription>
            {config.environment
              ? t(
                  "admin.events.publish.subtitleWithEnv",
                  "Publishes on the {{environment}} bus. Whatever consumes it does the real work.",
                  { environment: config.environment },
                )
              : t(
                  "admin.events.publish.subtitle",
                  "Whatever consumes this does the real work.",
                )}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-6">
          <Tabs value={mode} onValueChange={(value) => setMode(value as PublishMode)}>
            <TabsList variant="line">
              <TabsTrigger value="template" disabled={isPublishing}>
                {t("admin.events.publish.mode.template", "From template")}
              </TabsTrigger>
              <TabsTrigger value="custom" disabled={isPublishing}>
                {t("admin.events.publish.mode.custom", "From scratch")}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {mode === "template" ? (
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                {t("admin.events.publish.event", "Event")}
              </legend>
              {TRIGGERABLE_EVENTS.map((entry) => (
                <EventChoice
                  key={entry.eventName}
                  entry={entry}
                  isSelected={entry.eventName === eventName}
                  isDisabled={isPublishing}
                  onSelect={() => pick(entry)}
                />
              ))}
            </fieldset>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("admin.events.publish.topic", "Topic")}
                </span>
                <Select
                  value={topicFilter}
                  disabled={isPublishing}
                  onValueChange={(value) => pickTopic(value as DomainTopic | typeof ALL_TOPICS)}
                >
                  <SelectTrigger aria-label={t("admin.events.publish.topic", "Topic")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_TOPICS}>
                      {t("admin.events.publish.allTopics", "All topics")}
                    </SelectItem>
                    {topicGroups.map((group) => (
                      <SelectItem
                        key={group.topic}
                        value={group.topic}
                        className="font-mono text-[11px]"
                      >
                        {group.topic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "admin.events.publish.topicHint",
                    "The topic follows from the event name — this only narrows the list.",
                  )}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("admin.events.publish.event", "Event")}
                </span>
                <SearchableCombobox
                  groups={visibleTopicGroups.map((group) => ({
                    heading: group.topic,
                    items: group.eventNames,
                  }))}
                  getKey={(name) => name}
                  getLabel={(name) => name}
                  value={customEventName}
                  onSelect={(name) => setCustomEventName(name)}
                  disabled={isPublishing}
                  placeholder={t("admin.events.publish.customEventPlaceholder", "Pick an event…")}
                  searchPlaceholder={t(
                    "admin.events.publish.customSearchPlaceholder",
                    "Search events…",
                  )}
                  emptyMessage={t("admin.events.publish.customEmpty", "No event matches.")}
                  ariaLabel={t("admin.events.publish.event", "Event")}
                  triggerClassName="font-mono text-[11px]"
                  itemClassName="font-mono text-[11px]"
                />
              </div>
            </div>
          )}

          {mode === "template" && selected && hasPayloadFields(selected) ? (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="event-publish-payload"
                className="text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                {t("admin.events.publish.payload", "Payload")}
              </label>
              <Textarea
                id="event-publish-payload"
                value={payloadText}
                rows={PAYLOAD_ROWS}
                spellCheck={false}
                disabled={isPublishing}
                onChange={(changeEvent) => {
                  setPayloadText(changeEvent.target.value);
                  setIsPayloadEdited(true);
                }}
                className="resize-y font-mono text-[11px] leading-[1.6]"
              />
              {check && !check.ok ? (
                <p
                  className={cn(
                    "text-[11px]",
                    isPayloadEdited ? "text-error" : "text-muted-foreground",
                  )}
                >
                  {payloadIssueText(check.issue, isPayloadEdited, t)}
                </p>
              ) : null}
            </div>
          ) : null}

          {mode === "custom" ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="event-publish-custom-payload"
                  className="text-[11px] uppercase tracking-wider text-muted-foreground"
                >
                  {t("admin.events.publish.payload", "Payload")}
                </label>
                <div className="flex items-center gap-0.5">
                  {lastShapeText ? (
                    <PayloadAction
                      isDisabled={isPublishing || customPayloadText === lastShapeText}
                      title={t(
                        "admin.events.publish.useLastShapeHint",
                        "Fills in the field names from the last event of this type, with the values blanked.",
                      )}
                      onClick={() => setCustomPayloadText(lastShapeText)}
                    >
                      {t("admin.events.publish.useLastShape", "Use last shape")}
                    </PayloadAction>
                  ) : null}
                  <PayloadAction
                    isDisabled={
                      isPublishing ||
                      formattedCustomPayload === null ||
                      formattedCustomPayload === customPayloadText
                    }
                    onClick={() => {
                      if (formattedCustomPayload !== null) {
                        setCustomPayloadText(formattedCustomPayload);
                      }
                    }}
                  >
                    {t("admin.events.publish.format", "Format")}
                  </PayloadAction>
                </div>
              </div>
              <Textarea
                id="event-publish-custom-payload"
                value={customPayloadText}
                rows={PAYLOAD_ROWS}
                spellCheck={false}
                disabled={isPublishing}
                placeholder="{}"
                aria-invalid={!customCheck.ok}
                aria-describedby={CUSTOM_PAYLOAD_STATUS_ID}
                onChange={(changeEvent) => setCustomPayloadText(changeEvent.target.value)}
                className="resize-y font-mono text-[11px] leading-[1.6]"
              />
              <p
                id={CUSTOM_PAYLOAD_STATUS_ID}
                aria-live="polite"
                className={cn(
                  "text-[11px]",
                  customCheck.ok ? "text-muted-foreground" : "text-error",
                )}
              >
                {customCheck.ok
                  ? customFieldCount === 0
                    ? t("admin.events.publish.payloadEmpty", "Empty payload.")
                    : t("admin.events.publish.payloadValid", "Valid JSON — {{count}} fields", {
                        count: customFieldCount,
                      })
                  : payloadIssueText(customCheck.issue, true, t)}
              </p>
            </div>
          ) : null}

          {mode === "custom" || selected?.impact === TriggerableEventImpacts.HEAVY ? (
            <p
              className={cn(
                "rounded-md px-3 py-2 text-xs",
                isProduction
                  ? "bg-error-subtle text-error-subtle-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <Icon name="alert-circle" size="xs" className="mr-1.5 inline align-[-2px]" aria-hidden />
              {mode === "custom"
                ? isProduction
                  ? t(
                      "admin.events.publish.customWarningProduction",
                      "No template validates this payload. It publishes on the production bus as-is and cannot be undone.",
                    )
                  : t(
                      "admin.events.publish.customWarning",
                      "No template validates this payload — it is sent as-is, and whatever consumes it does real work.",
                    )
                : isProduction
                  ? t(
                      "admin.events.publish.heavyProduction",
                      "This runs against production data and cannot be undone.",
                    )
                  : t(
                      "admin.events.publish.heavy",
                      "This does real work — writes, upstream calls, and time.",
                    )}
            </p>
          ) : null}

          {publishError ? (
            <p className="rounded-md bg-error-subtle px-3 py-2 text-xs text-error-subtle-foreground">
              {publishError}
            </p>
          ) : null}
        </SheetBody>

        <SheetFooter className="justify-end px-6">
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-sm text-xs"
            disabled={isPublishing}
            onClick={() => onOpenChange(false)}
          >
            {t("admin.events.publish.cancel", "Cancel")}
          </Button>
          <Button
            size="sm"
            className="h-8 rounded-sm text-xs"
            disabled={!canPublish}
            onClick={publish}
          >
            {isPublishing
              ? t("admin.events.publish.publishing", "Publishing…")
              : t("admin.events.publish.confirm", "Publish")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * An editor affordance rather than a page action — it stays quiet until
 * hovered so the payload itself keeps the weight in this section.
 */
function PayloadAction({
  isDisabled,
  title,
  onClick,
  children,
}: {
  isDisabled: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      title={title}
      disabled={isDisabled}
      onClick={onClick}
      className="h-6 rounded-sm px-2 text-[11px] font-normal text-muted-foreground hover:text-foreground"
    >
      {children}
    </Button>
  );
}

function EventChoice({
  entry,
  isSelected,
  isDisabled,
  onSelect,
}: {
  entry: TriggerableEvent;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <label
      className={cn(
        "flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2.5 transition-colors",
        isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted",
        isDisabled && "cursor-not-allowed opacity-60",
      )}
    >
      <span className="flex items-baseline gap-2">
        <input
          type="radio"
          name="event-publish-choice"
          className="sr-only"
          checked={isSelected}
          disabled={isDisabled}
          onChange={onSelect}
        />
        <span className="min-w-0 flex-1 break-all font-mono text-[11px]">{entry.eventName}</span>
        {entry.impact === TriggerableEventImpacts.HEAVY ? (
          <span className="shrink-0 text-[10px] uppercase tracking-wider text-warning">
            {t("admin.events.publish.impact.heavy", "does real work")}
          </span>
        ) : (
          <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("admin.events.publish.impact.safe", "safe")}
          </span>
        )}
      </span>
      <span className="text-xs text-muted-foreground">{entry.summary}</span>
      <span className="text-[11px] text-muted-foreground">
        {t("admin.events.publish.handledBy", "Handled by {{service}}", {
          service: entry.handledBy,
        })}
      </span>
    </label>
  );
}

function payloadIssueText(
  issue: PayloadIssue,
  isEdited: boolean,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  switch (issue.kind) {
    case "invalid-json":
      // The offset is the whole value of the message in a hand-typed payload —
      // "not valid JSON" alone makes the operator re-read every line.
      return issue.at
        ? t(
            "admin.events.publish.error.jsonAt",
            "Invalid JSON at line {{line}}, column {{column}}.",
            { line: issue.at.line, column: issue.at.column },
          )
        : t("admin.events.publish.error.json", "This is not valid JSON.");
    case "not-an-object":
      return t("admin.events.publish.error.object", "The payload must be a JSON object.");
    case "schema":
      if (!issue.path) return issue.message;
      // Before anyone has typed, the blank sample field is a prompt rather than
      // a mistake, so it asks for the value instead of quoting the validator.
      return isEdited
        ? t("admin.events.publish.error.field", "{{field}}: {{message}}", {
            field: issue.path,
            message: issue.message,
          })
        : t("admin.events.publish.error.fill", "Fill in {{field}}.", { field: issue.path });
  }
}
